// Showing API errors. A plan limit gets its own message with a "Request access" button.
import { Alert, Button, Group, Modal, Stack, Text, TextInput, Textarea } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError, formatLimit, post } from "../lib/api";
import { useSpecs } from "../lib/hooks";

export function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function notifyError(e: unknown, title = "Something went wrong") {
  notifications.show({ color: "red", title: e instanceof ApiError && e.isLimit ? "Not included in your plan" : title, message: errorText(e) });
}

export function notifyOk(message: string) {
  notifications.show({ color: "teal", message });
}

export function RequestAccessModal({ limitKey, opened, onClose, suggested }: {
  limitKey: string; opened: boolean; onClose: () => void; suggested?: unknown;
}) {
  const specs = useSpecs();
  const spec = specs[limitKey];
  const [value, setValue] = useState(suggested != null && typeof suggested !== "boolean" ? String(suggested) : "");
  const [message, setMessage] = useState("");
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => post("/me/access-requests", {
      limit_key: limitKey,
      requested_value: spec?.kind === "bool" ? null : value || null,
      message,
    }),
    onSuccess: () => {
      notifyOk("Request sent. An administrator will review it.");
      qc.invalidateQueries({ queryKey: ["access-requests"] });
      onClose();
    },
    onError: (e) => notifyError(e, "Request not sent"),
  });
  return (
    <Modal opened={opened} onClose={onClose} title="Request access" centered>
      <Stack>
        <Text>
          <b>{spec?.label ?? limitKey}</b>
          {spec && spec.kind !== "bool" && <> (your plan: {formatLimit(spec, spec.plans.base)} on Base)</>}
        </Text>
        {spec && spec.kind !== "bool" && (
          <TextInput label={`Value you need${spec.unit ? ` (${spec.unit})` : ""}`} value={value}
            onChange={(e) => setValue(e.currentTarget.value)} />
        )}
        <Textarea label="What is it for?" placeholder="e.g. a global search for the MoS2 fit" minRows={3} autosize
          value={message} onChange={(e) => setMessage(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button loading={m.isPending} onClick={() => m.mutate()}>Send request</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** An error box. For a plan limit it explains the limit and offers "Request access". */
export function ErrorBox({ error }: { error: unknown }) {
  const [open, setOpen] = useState(false);
  if (!error) return null;
  const limit = error instanceof ApiError && error.isLimit ? error : null;
  return (
    <>
      <Alert color={limit ? "orange" : "red"} title={limit ? "Not included in your plan" : "Error"} my="sm">
        <Stack gap="xs">
          <Text size="sm">{errorText(error)}</Text>
          {limit && (
            <Group>
              <Button size="xs" variant="light" onClick={() => setOpen(true)}>Request access</Button>
            </Group>
          )}
        </Stack>
      </Alert>
      {limit && limit.limit && (
        <RequestAccessModal limitKey={limit.limit} opened={open} onClose={() => setOpen(false)} suggested={limit.requested} />
      )}
    </>
  );
}
